import { groupBy, sortedUniqBy } from 'lodash';
import { CartItem, PricingInfo, TransactionRule } from '../components/types';
import { Order, Transaction, TransactionItem } from './typings';

interface ProductPriceRule {
  name: string;
  priority: number;
  new_price: number;
  entity_amount: number;
  applied: boolean;
}

interface ProductInfo {
  id: number;
  name: string;
  vat: string;
  price: number;
}

export interface CartLine {
  final_price: number;
  product: ProductInfo;
  rules: ProductPriceRule[];
}

export interface RawPriceData {
  total_price: number;
  rules: TransactionRule[];
  items: {
    total_price: number;
    lines: CartLine[];
  };
}

export interface FormattedCartPricingData {
  totalPrice: number;
  items: CartItem[];
  transactionRules: TransactionRule[];
}

export function formatCartPriceData(rawPriceData: RawPriceData): FormattedCartPricingData {
  const itemData: { [id: number]: CartItem } = {};

  rawPriceData.items.lines.forEach((item: CartLine) => {
    const productId = item.product.id;

    const hasSubsidy = item.rules.some((rule: ProductPriceRule) => rule.applied);

    if (!itemData[productId]) {
      // if we have not yet seen the product, add it to itemData
      itemData[productId] = {
        id: productId,
        name: item.product.name,
        total: { price: item.final_price, quantity: 1 },
        ...(hasSubsidy
          ? { subsidies: [{ price: item.final_price, quantity: 1 }] }
          : { default: { price: item.final_price, quantity: 1 }, subsidies: [] }),
      };
    } else {
      // if we have already seen this product before...
      const productInfo = itemData[productId];

      // add its price and quantity to the cart item total
      productInfo.total.price += item.final_price;
      productInfo.total.quantity += 1;

      // add the pricing info to the right field
      if (hasSubsidy) {
        const subsidyIndex = productInfo.subsidies.findIndex(
          (subsidy: PricingInfo) => subsidy.price === item.final_price,
        );

        // check if this subsidized price has been seen previously
        if (subsidyIndex < 0) {
          // if not add the subsidized price to the list
          productInfo.subsidies.push({ price: item.final_price, quantity: 1 });
        } else {
          // if we have seen it, increment the quantity of the relevant subsidy
          productInfo.subsidies[subsidyIndex].quantity += 1;
        }
      } else if (productInfo.default) {
        // if a previous instance of this product also had default pricing,
        // increment the quantity of items with the default price
        productInfo.default.quantity += 1;
      } else {
        // if this is the first time we're seeing this product with a default price,
        // add the default price
        productInfo.default = {
          price: item.final_price,
          quantity: 1,
        };
      }
    }
  });

  return {
    totalPrice: rawPriceData.total_price,
    items: Object.values(itemData),
    transactionRules: rawPriceData.rules.map((rule: TransactionRule) => ({
      name: rule.name,
      amount: rule.amount,
    })),
  };
}

interface RawTransactionItem extends Omit<TransactionItem, 'quantity'> {}

interface RawTransaction extends Omit<Transaction, 'created_at' | 'items' | 'priced_at'> {
  created_at: string;
  items: RawTransactionItem[];
  priced_at: string;
}

export interface RawOrder extends Omit<Order, 'transaction'> {
  transaction: RawTransaction;
}

export function formatOrder(order: RawOrder): Order {
  const groupItems = groupBy(order.transaction.items, ({ product_id }) => product_id);
  const quantifiedItems = sortedUniqBy(order.transaction.items, ({ product_id }) => product_id).map(
    item => ({
      ...item,
      quantity: groupItems[item.product_id].length,
      saved_price_monetary_amount: {
        amount: groupItems[item.product_id].reduce(
          (total, current) => total + current.saved_price_monetary_amount.amount,
          0,
        ),
        currency: order.transaction.amount.currency,
      },
    }),
  );
  return {
    ...order,
    transaction: {
      ...order.transaction,
      items: quantifiedItems,
    },
  };
}
